import React, { useState, useRef, useCallback } from 'react';
import { Dialog } from './common/Dialog';
import { Button } from './common/Button';
import { validateMdsUrl } from '../utils/urlValidator';
import logger from '../utils/logger';
import { FetchProgress } from '../services/csvProcessor';
import * as styles from '../styles/components/open-from-url-dialog.css';

interface OpenFromUrlDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onOpen: (url: string, options?: { signal?: AbortSignal; onProgress?: (progress: FetchProgress) => void }) => Promise<void>;
}

/**
 * URL入力ダイアログ
 * CSVファイルをURLから読み込むためのダイアログ
 */
export function OpenFromUrlDialog({
  isOpen,
  onClose,
  onOpen,
}: OpenFromUrlDialogProps) {
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [progress, setProgress] = useState<FetchProgress | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // URLを検証して状態を更新する共通関数
  const validateAndSetUrl = (newUrl: string) => {
    setUrl(newUrl);
    setError(null);
    setWarning(null);

    // リアルタイムでURL検証
    if (newUrl.trim()) {
      const validation = validateMdsUrl(newUrl);
      if (!validation.allowed) {
        setError(validation.reason || 'Invalid URL');
      } else if (validation.needsWarning) {
        setWarning(validation.reason || 'This URL may not be trusted');
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    validateAndSetUrl(e.target.value);
  };

  const handleProgress = useCallback((p: FetchProgress) => {
    setProgress(p);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }

    // URL形式チェック
    try {
      new URL(url);
    } catch {
      setError('Invalid URL format');
      return;
    }

    // AbortControllerを作成
    abortControllerRef.current = new AbortController();

    try {
      setIsLoading(true);
      setError(null);
      setProgress(null);
      logger.info('[OpenFromUrlDialog] Opening URL:', url);
      await onOpen(url, {
        signal: abortControllerRef.current.signal,
        onProgress: handleProgress,
      });
      // 成功したらダイアログを閉じてフォームをリセット
      setUrl('');
      setProgress(null);
      onClose();
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        logger.info('[OpenFromUrlDialog] Request cancelled by user');
        setError('Request cancelled');
      } else {
        logger.error('[OpenFromUrlDialog] Error opening URL:', err);
        setError(err instanceof Error ? err.message : 'Failed to open URL');
      }
    } finally {
      setIsLoading(false);
      setProgress(null);
      abortControllerRef.current = null;
    }
  };

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const handleClose = () => {
    if (isLoading) {
      handleCancel();
    }
    setUrl('');
    setError(null);
    setWarning(null);
    setProgress(null);
    onClose();
  };

  const getProgressLabel = () => {
    if (!progress) return '';
    switch (progress.phase) {
      case 'connecting':
        return 'Connecting...';
      case 'downloading':
        if (progress.bytesTotal) {
          const mb = (progress.bytesLoaded || 0) / 1024 / 1024;
          const totalMb = progress.bytesTotal / 1024 / 1024;
          return `Downloading... ${mb.toFixed(1)}/${totalMb.toFixed(1)} MB`;
        }
        return `Downloading... ${((progress.bytesLoaded || 0) / 1024).toFixed(0)} KB`;
      case 'parsing':
        return 'Parsing CSV...';
      default:
        return 'Loading...';
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title="Open from URL"
      width="lg"
      closeOnOverlayClick={!isLoading}
      showCloseButton={!isLoading}
      data-testid="open-from-url-dialog"
      footer={
        <>
          {isLoading ? (
            <Button
              variant="secondary"
              onClick={handleCancel}
              data-testid="cancel-fetch-button"
            >
              Cancel
            </Button>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={handleClose}
              >
                Close
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleSubmit}
                disabled={!url.trim() || !!error}
              >
                Open
              </Button>
            </>
          )}
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <div className={styles.formGroup}>
          <label htmlFor="csv-url" className={styles.label}>
            CSV URL
          </label>
          <input
            id="csv-url"
            type="url"
            value={url}
            onChange={handleChange}
            placeholder="https://example.com/data.csv"
            disabled={isLoading}
            autoFocus
            className={`${styles.urlInput} ${error ? styles.urlInputError : ''}`}
            data-testid="csv-url-input"
          />
        </div>

        {isLoading && progress && (
          <div className={styles.progressContainer} data-testid="progress-container">
            <div className={styles.progressLabel}>
              <span>{getProgressLabel()}</span>
              {progress.percent !== undefined && (
                <span>{progress.percent}%</span>
              )}
            </div>
            <div className={styles.progressBar}>
              {progress.percent !== undefined ? (
                <div
                  className={styles.progressFill}
                  style={{ width: `${progress.percent}%` }}
                />
              ) : (
                <div className={styles.progressIndeterminate} />
              )}
            </div>
          </div>
        )}

        {warning && !isLoading && (
          <div className={styles.warningBox}>
            <span className={styles.warningLabel}>Warning: </span>
            <span className={styles.warningText}>{warning}</span>
          </div>
        )}

        {error && !isLoading && (
          <div className={styles.errorBox} data-testid="url-error">
            {error}
          </div>
        )}

        {!isLoading && (
          <div className={styles.requirementsBox}>
            <p className={styles.requirementsTitle}>Requirements:</p>
            <ul className={styles.requirementsList}>
              <li>URL must return CSV or TSV data</li>
              <li>Server must allow CORS (Cross-Origin Resource Sharing)</li>
              <li>Maximum file size: 10MB</li>
            </ul>
          </div>
        )}
      </form>
    </Dialog>
  );
}
